import {autobind} from 'core-decorators';

import {Component} from 'inferno'; // eslint-disable-line @typescript-eslint/no-unused-vars
import {Button, CustomComponent} from '../../../common/components';
import {
    langDirDenyList, langOrLocaleIncluded, localeInternalList, localesSupported,
} from '../../../common/translation';

import {subscribe} from '../../../../replete';

import {BaseSearchUIValue} from '../../../common/components/BaseSearchBar';
import {BaseSearchBox, BaseSearchBoxProps} from '../../pages/gif-maker/components/TagSearchBox';

import type {LocaleSuggestion} from '../../../common/config';

import './LanguageSelectionDialog.scss';

const storageServiceKey = 'locale';

export function getChosenLocale(context: jsx.AppGlobals): string|null {
    const locale = context.storageService.getItem(storageServiceKey);
    if (locale && langOrLocaleIncluded(locale, langDirDenyList)) {
        return null;
    }
    return locale;
}

export function setChosenLocale(context: jsx.AppGlobals, chosenLocale: string) {
    context.storageService.setItem(storageServiceKey, chosenLocale);
}

export function addLanguagesToContext(context: jsx.AppGlobals) {
    const gettextSub = context.gtService.gettextSub;
    // Everything that uses gettext* needs to be in a JSX component or request
    // handler.
    const languages = [
        ['af', gettextSub('Afrikaans')],
        ['am', gettextSub('Amharic')],
        ['ar', gettextSub('Arabic')],
        ['ar_EG', gettextSub('Arabic (Egypt)')],
        ['ar_JO', gettextSub('Arabic (Jordan)')],
        ['ar_MA', gettextSub('Arabic (Morocco)')],
        ['ar_SA', gettextSub('Arabic (Saudi Arabia)')],
        [
            'ar_XB',
            // Translators: Same as Pseudo-Bidi, a fake locale that's used for
            // Translators: testing RTL layouts, not visible to end users.
            gettextSub('Fakebidi'),
        ],
        ['az', gettextSub('Azerbaijani')],
        ['be', gettextSub('Belarussian')],
        ['bg', gettextSub('Bulgarian')],
        ['bn', gettextSub('Bangla')],
        ['bs', gettextSub('Bosnian')],
        ['ca', gettextSub('Catalan')],
        ['cs', gettextSub('Czech')],
        ['da', gettextSub('Danish')],
        ['de', gettextSub('German')],
        ['de_AT', gettextSub('German (Austria)')],
        ['de_CH', gettextSub('German (Switzerland)')],
        ['dv', gettextSub('Divehi')],
        ['el', gettextSub('Greek')],
        ['en', gettextSub('English')],
        ['en_AU', gettextSub('English (Austrailia)')],
        ['en_CA', gettextSub('English (Canada)')],
        ['en_GB', gettextSub('English (Great Britain)')],
        ['en_IE', gettextSub('English (Ireland)')],
        ['en_IN', gettextSub('English (India)')],
        ['en_NZ', gettextSub('English (New Zealand)')],
        ['en_SG', gettextSub('English (Singapore)')],
        [
            'en_XA',
            // Translators: A fake locale that's used for testing accented
            // Translators: characters, not visible to end users.
            gettextSub('English (Pseudo Accents)'),
        ],
        [
            'en_XC',
            // Translators: Cracked English build:
            // Translators: https://g3doc.corp.google.com/java/com/google/transconsole/tools/pseudolocalizer/g3doc/localename_bp.md#cracked_build
            gettextSub('English (Pseudo Cracked)'),
        ],
        ['en_ZA', gettextSub('English (South Africa)')],
        ['es', gettextSub('Spanish')],
        ['es_419', gettextSub('Spanish (Latin America)')],
        ['es_AR', gettextSub('Spanish (Argentina)')],
        ['es_BO', gettextSub('Spanish (Bolivia)')],
        ['es_CL', gettextSub('Spanish (Chile)')],
        ['es_CO', gettextSub('Spanish (Colombia)')],
        ['es_CR', gettextSub('Spanish (Costa Rica)')],
        ['es_DO', gettextSub('Spanish (Dominican Republic)')],
        ['es_EC', gettextSub('Spanish (Ecuador)')],
        ['es_GT', gettextSub('Spanish (Guatemala)')],
        ['es_HN', gettextSub('Spanish (Honduras)')],
        ['es_MX', gettextSub('Spanish (Mexico)')],
        ['es_NI', gettextSub('Spanish (Nicaragua)')],
        ['es_PA', gettextSub('Spanish (Panama)')],
        ['es_PE', gettextSub('Spanish (Peru)')],
        ['es_PR', gettextSub('Spanish (Puerto Rico)')],
        ['es_PY', gettextSub('Spanish (Paraguay)')],
        ['es_SV', gettextSub('Spanish (El Salvador)')],
        ['es_US', gettextSub('Spanish (United States)')],
        ['es_UY', gettextSub('Spanish (Uruguay)')],
        ['es_VE', gettextSub('Spanish (Venezuela)')],
        ['et', gettextSub('Estonian')],
        ['eu', gettextSub('Basque')],
        ['fa', gettextSub('Persian')],
        ['fi', gettextSub('Finnish')],
        ['fil', gettextSub('Filipino')],
        ['fr', gettextSub('French')],
        ['fr_CA', gettextSub('French (Canadian)')],
        ['fr_CH', gettextSub('French (Switzerland)')],
        ['gl', gettextSub('Galician')],
        ['gu', gettextSub('Gujarati')],
        ['he', gettextSub('Hebrew')],
        ['hi', gettextSub('Hindi')],
        ['hr', gettextSub('Croatian')],
        ['hu', gettextSub('Hungarian')],
        ['hy', gettextSub('Armenian')],
        ['id', gettextSub('Indonesian')],
        ['is', gettextSub('Icelandic')],
        ['it', gettextSub('Italian')],
        ['ja', gettextSub('Japanese')],
        ['ka', gettextSub('Georgian')],
        ['kk', gettextSub('Kazakh')],
        ['km', gettextSub('Khmer')],
        ['kn', gettextSub('Kannada')],
        ['ko', gettextSub('Korean')],
        ['ku', gettextSub('Kurdish')],
        ['ky', gettextSub('Kyrgyz')],
        ['lo', gettextSub('Lao')],
        ['lt', gettextSub('Lithuanian')],
        ['lv', gettextSub('Latvian')],
        ['mk', gettextSub('Macedonian')],
        ['ml', gettextSub('Malayalam')],
        ['mn', gettextSub('Mongolian')],
        ['mo', gettextSub('Moldovan')],
        ['mr', gettextSub('Marathi')],
        ['ms', gettextSub('Malay')],
        ['my', gettextSub('Burmese')],
        ['ne', gettextSub('Nepali')],
        ['nl', gettextSub('Dutch')],
        ['no', gettextSub('Norwegian')],
        ['pa', gettextSub('Punjabi')],
        ['pl', gettextSub('Polish')],
        ['pt', gettextSub('Portuguese')],
        ['pt_BR', gettextSub('Portuguese (Brazil)')],
        ['pt_PT', gettextSub('Portuguese (Portugal)')],
        ['ro', gettextSub('Romanian')],
        ['ru', gettextSub('Russian')],
        ['si', gettextSub('Sinhala')],
        ['sk', gettextSub('Slovak')],
        ['sl', gettextSub('Slovenian')],
        ['sq', gettextSub('Albanian')],
        ['sr', gettextSub('Serbian')],
        ['sr_Latn', gettextSub('Serbian (Latin)')],
        ['sv', gettextSub('Swedish')],
        ['sw', gettextSub('Swahili')],
        ['ta', gettextSub('Tamil')],
        ['te', gettextSub('Telugu')],
        ['th', gettextSub('Thai')],
        ['tl', gettextSub('Tagalog')],
        ['tr', gettextSub('Turkish')],
        ['uk', gettextSub('Ukrainian')],
        ['ur', gettextSub('Urdu')],
        ['uz', 'Uzbek', `O'zbek`, gettextSub('Uzbek')],
        ['vi', gettextSub('Vietnamese')],
        ['zh_CN', gettextSub('Chinese (Simplified)')],
        ['zh_HK', gettextSub('Chinese (Hong Kong)')],
        ['zh_TW', gettextSub('Chinese (Traditional)')],
        ['zu', gettextSub('Zulu')],
    ].filter((lang) => {
        return (
            !langOrLocaleIncluded(lang[0], langDirDenyList) &&
            (
                context.featureFlags.enableInternalLocales ||
                !langOrLocaleIncluded(lang[0], localeInternalList)
            )
        );
    });
    function getLocaleSuggestions(
        [code, name]: [string, string]): LocaleSuggestion {
        let langConfig = localesSupported[code];

        if (!langConfig) {
            context.request.console.error(
                `${code} is not in localesSupported`);
            langConfig = [name, name];
        }
        const [englishName, localName] = langConfig;
        const localeSuggestion = [code, name, englishName, localName];

        // For case-insensitive search
        const searchKey = localeSuggestion.join('|').toLowerCase();
        localeSuggestion.push(searchKey);
        return localeSuggestion as LocaleSuggestion;
    }
    context.languages = languages.map(getLocaleSuggestions);
    context.languages.sort((a, b) => {
        return a[3] > b[3] ? 1 : -1;
    });
}

export function redirectToLang(context: jsx.AppGlobals, selectedLang: string) {
    const currentPath = context.gtService.delocalizeUrlPath(
        context.router.history.location.pathname);
    const localePrefix = selectedLang !== 'en' ?
        `/${selectedLang.replace('_', '-')}` : '';
    window.location.href = `${localePrefix}${currentPath}${
        context.router.history.location.search}`;
}

interface LanguageSearchBoxProps extends BaseSearchBoxProps {
    idx: number;
    languages: LocaleSuggestion[];
    langSelectionDialog: any;
}

export class LanguageSearchBoxValue implements BaseSearchUIValue {
    locale: LocaleSuggestion;

    constructor(locale) {
        this.locale = locale;
    }
    key() {
        return this.locale[0];
    }
    toUserString() {
        return `${this.locale[3]} / ${this.locale[2]}`;
    }
}

@subscribe({
    isMobile: ['ui.isMobile'],
})
export class LanguageSearchBox extends BaseSearchBox<
    LanguageSearchBoxValue, LanguageSearchBoxProps> {
    constructor(props: jsx.Props, context: jsx.AppGlobals) {
        super(props, context);
        this.dropdownEnabledOnFocus = true;
        this.focusAfterSelect = false;
        this.canAddSuggestion = false;
        this.scrollSuggestions = true;
    }
    componentDidMount() {
        if (!this.props.isMobile && this.props.idx === 0) {
            const s = '';
            this.setState({value: s});
            const reqValue = s;
            this.fetchSuggestions(reqValue).then((suggestions) => {
                this.defaultSuggestions = suggestions;
            });
        }
    }
    stringToValue(s: string): LanguageSearchBoxValue {
        for (const lang in this.props.languages) {
            if (lang[0] === s) {
                return new LanguageSearchBoxValue(lang);
            }
        }
        return new LanguageSearchBoxValue(['', '', '', '', '']);
    }
    fetchSuggestions(reqValue: string): Promise<LanguageSearchBoxValue[]> {
        // only update suggestions if search term === current search term
        if (reqValue === this.state.value.trim()) {
            const lowerReqValues = reqValue.toLowerCase().split(/\s+/);
            const suggestions = this.props.languages.filter((langItem) => {
                return lowerReqValues.every((lowerReqValue) => {
                    return langItem[4].includes(lowerReqValue);
                });
            });
            const first = [];
            const second = [];
            const remainder = [];
            for (const suggestion of suggestions) {
                if (suggestion[3].toLowerCase().startsWith(
                    lowerReqValues[0])) {
                    first.push(suggestion);
                } else if (suggestion[2].toLowerCase().startsWith(
                    lowerReqValues[0])) {
                    second.push(suggestion);
                } else {
                    remainder.push(suggestion);
                }
            }

            const sortedSuggestions = [
                ...first,
                ...second,
                ...remainder,
            ].map((locale) => {
                return new LanguageSearchBoxValue(locale);
            });
            this.setSuggestions(sortedSuggestions);
            return Promise.resolve(sortedSuggestions);
        }
        return Promise.resolve(this.defaultSuggestions);
    }
    onDone(suggestion: string) {
        this.props.langSelectionDialog.setState({
            selectedLocale: suggestion});
    }
    @autobind
    placeholder() {
        const gettextSub = this.context.gtService.gettextSub;

        // Translators: Search input placeholder text.
        return gettextSub('Search for Language');
    }
    getSuggestionListHeader(text: string): string {
        const gettextSub = this.context.gtService.gettextSub;

        return gettextSub('Suggested Language');
    }
    render() {
        console.log('this.state.value', this.state.value);
        const text = this.state.value;

        return <div
            className={`LanguageSearchBox BaseSearchBox${
                this.scrollSuggestions ? ' scrollable' : ''}`}>
            <div className="lang-input-wrapper">
                <input
                    ref={this.setInputElement}
                    id={`lang_${this.props.idx}`}
                    className="lang-input"
                    onInput={this.handleOnInput}
                    onBlur={this.handleOnBlur}
                    onFocus={this.handleOnFocus}
                    onKeyDown={this.handleOnKeyDown}
                    onMouseOver={this.clearSuggestionSelection}
                    onPaste={this.handlePaste}
                    value={text}
                    placeholder={this.placeholder()}
                    spellCheck={false}
                    autoCorrect={this.iOS ? 'off' : 'on'}
                    autoCapitalize="off"
                    autoComplete="off"
                />
            </div>
            { this.state.dropdownEnabled && this.state.focused &&
                this.renderSuggestionDropDown() }
        </div>;
    }
}

export class LanguageSelectionDialog extends CustomComponent {
    constructor(props: jsx.Props, context: jsx.AppGlobals) {
        super(props, context);
        const gtService = this.context.gtService;
        this.state = {
            ...this.state,
            selectedLocale: gtService.gt.locale,
        };
    }
    getSelectedLocaleTuple() {
        const {selectedLocale} = this.state;
        for (const locale of this.context.languages) {
            if (locale[0] === selectedLocale) {
                return locale;
            }
        }
        console.warn(`Selected locale ${selectedLocale} is missing from list.`);
    }
    @autobind
    onConfirm() {
        const selectedLocaleTuple = this.getSelectedLocaleTuple();
        const selectedLocale = selectedLocaleTuple ?
            selectedLocaleTuple[0] : 'en';
        setChosenLocale(this.context, selectedLocale);
        redirectToLang(this.context, selectedLocale);
    }
    render() {
        const gtService = this.context.gtService;
        const gettextSub = this.context.gtService.gettextSub;
        const gettextSubComponent = this.context.gtService.gettextSubComponent;

        const selectedLang = this.getSelectedLocaleTuple();
        const selectedLangLabel = selectedLang ?
            new LanguageSearchBoxValue(selectedLang).toUserString() :
            gettextSub('None');
        const isCurrentLocaleSelected = (
            this.state.selectedLocale === gtService.gt.locale);

        return (
            <div id="language-selection-dialog">
                <h2>{gettextSub('Language')}</h2>
                <LanguageSearchBox
                    languages={this.context.languages}
                    idx={0}
                    langSelectionDialog={this}
                />
                <h3>{gettextSub('Confirm language selection?')}</h3>
                <p>{
                    gettextSubComponent('You have selected {lang}',
                        {lang: <strong>{selectedLangLabel}</strong>})
                }</p>
                <p>{gettextSub(
                    'Clicking yes will reload Tenor in the language you have selected.',
                )}</p>
                <div className="button-row">
                    <Button
                        className="deny-button"
                        onClick={this.props.dialog.close}>
                        {gettextSub('No')}
                    </Button>
                    <Button
                        className="confirm-button"
                        onClick={this.onConfirm}
                        disabled={isCurrentLocaleSelected}>
                        {gettextSub('Yes')}
                    </Button>
                </div>
            </div>
        );
    }
}
